import {Component, EventEmitter, Input, Output} from '@angular/core';
import {TreeNode} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {ECpmAEPluginFunctionTypeApiEnum} from '../../../../../gen/cpm-ae-api-ts/src/enums/ae-global/cpmae-plugins.api-enums';
import {
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {
    ECpmCTaskAttributes1ApiEnum,
    ECpmCTaskStatusApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCTaskApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {
    CpmCTaskInputOutputMappingApiSchema,
    CpmCTaskUpdateApiSchema
} from '../../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {CpmCTaskApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task.api-service';
import {IBaseComponent} from '../../../../../sdk/src/components/ibase/ibase.component';
import {
    ConfirmationModalComponent,
    ConfirmationModalConfig
} from '../../../../../sdk/src/modals/confirm-modal/confirm-modal.component';
import {
    JsonSchemaFormModalComponent,
    JsonSchemaFormModalResult
} from '../../../../../sdk/src/modals/json-schema-form-modal/json-schema-form-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {DateUtils} from '../../../../../sdk/src/utils/date.utils';
import {JsonSchemaUtils} from '../../../../../sdk/src/utils/json-schema.utils';
import {StringUtils} from '../../../../../sdk/src/utils/string.util';
import {TreeUtils} from '../../../../../sdk/src/utils/tree.utils';
import {TaskTreeViewExtension} from '../../../api-extensions/views/task-tree.view-extension';
import {SdkBridge} from '../../../sdk-bridge';
import {ObjectStoreEntriesTransientCache} from '../../../transient-cache/object-store-entries.transient-cache';
import {ActionJobsUtils} from '../../../utils/action-jobs.utils';
import {TaskOptionDisplay, TaskOptionsUtils} from '../../../utils/task-options.utils';
import {TaskUtils} from '../../../utils/task.utils';
import {
    ActionJobsFunctionIoEditor,
    ActionJobsFunctionIoEditorChangeEvent
} from '../../action-jobs-function-io-editor/action-jobs-function-io-editor.component';

/**
 * Task details component
 */
@Component({
    selector: 'app-task-details',
    templateUrl: './task-details.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule, ActionJobsFunctionIoEditor]
})
export class TaskDetailsComponent extends IBaseComponent {
    protected readonly ECpmCTaskTypeApiEnum = ECpmCTaskTypeApiEnum;
    protected readonly TaskUtils = TaskUtils;

    // ===============================================================
    // ===============================================================

    private readonly _taskStatusHumanReadable: string[] = Object.keys(ECpmCTaskStatusApiEnum).map((x) =>
        StringUtils.toHumanFormatedStr(x)
    );

    // ===============================================================
    // ===============================================================

    protected _canEditTask = false;
    protected _taskTreeItems?: TaskTreeViewExtension[];
    protected _functionsTree: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[] = [];

    protected _currentTask?: CpmCTaskApiModel;
    protected _currentTaskDependencies: (TaskTreeViewExtension | undefined)[] = [];
    protected _currentTaskDependants: (TaskTreeViewExtension | undefined)[] = [];
    protected _canEditStatus = false;
    protected _canEditDueDate = false;
    protected _canEditStartDate = false;
    protected _canEditEndDate = false;
    protected _taskStatusToHumanReadable = Object.fromEntries(
        Object.entries(ECpmCTaskStatusApiEnum).map(([key, value]) => [
            key as ECpmCTaskStatusApiEnum,
            StringUtils.toHumanFormatedStr(value)
        ])
    );

    protected _selectedPlugin?: CpmAEPluginSpecApiSchema;
    protected _selectedPluginFunction?: CpmAEPluginFunctionApiSchema;
    protected _inputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = {};
    protected _inputsMappingValid = false;
    protected _outputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = {};
    protected _outputsMappingValid = false;
    protected _objectStoreEntriesTransientCache?: ObjectStoreEntriesTransientCache;
    protected _options: TaskOptionDisplay[] = [];

    protected get _taskInputChangeConfirmation(): ConfirmationModalConfig {
        const confirmationConfig = new ConfirmationModalConfig();
        confirmationConfig.title = 'Update warning';
        confirmationConfig.message =
            'You are about to change an input value of a task that has already been executed.\n' +
            'Doing so will reset the status of this task, already executed dependent tasks, and already executed tasks contained in dependent groups.\n' +
            'Do you confirm?';
        confirmationConfig.severity = 'warn';
        confirmationConfig.confirmationConfig.severity = 'danger';
        return confirmationConfig;
    }

    // ===============================================================
    // ===============================================================

    @Input()
    public set tasksTreeItems(value: TaskTreeViewExtension[] | null) {
        if (value !== null) {
            this._taskTreeItems = value;
        }
    }
    @Input()
    public set currentTaskUUID(value: string | undefined) {
        this.showLoading = true;
        this._currentTask = undefined;

        if (value === null || value === undefined) {
            this.showLoading = false;
            return;
        }

        void this.loadTask(value);
    }

    @Output()
    public $selectTaskRequest = new EventEmitter<string>();

    @Output()
    public $refreshRequest = new EventEmitter<void>();

    // ===============================================================
    // ===============================================================

    /**
     * Gets the live status of the current task.
     */
    protected get currentTaskLiveStatus(): ECpmCTaskStatusApiEnum | undefined {
        if (!this._currentTask?.uuid) {
            return undefined;
        }

        return (
            this._taskTreeItems?.find((item) => item.uuid === this._currentTask?.uuid)?.status ??
            this._currentTask.status
        );
    }

    /**
     * Determines whether a warning should be shown when the input of the task is changed.
     */
    protected get shouldWarnResetOnChange(): boolean {
        const status = this.currentTaskLiveStatus;
        return TaskUtils.isExecutedStatus(status);
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */

    protected override async onReady(): Promise<void> {
        this._canEditTask = SdkBridge.isAuthorizedForRouteResourceUpdate(
            CpmCTaskApiService.endpointEdit(undefined, undefined, this.FAKE_UUID)
        );

        this._functionsTree = await ActionJobsUtils.getFunctionsTree(ECpmAEPluginFunctionTypeApiEnum.TASK);
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    public async refresh() {
        if (this._currentTask) await this.loadTask(this._currentTask?.uuid);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Load task by UUID
     * @param taskUUID UUID of the task to load
     */
    protected async loadTask(taskUUID: string, fromUpdate?: CpmCTaskApiModel) {
        this._currentTask = fromUpdate ?? (await firstValueFrom(CpmCTaskApiService.getTaskByUUID(taskUUID)));

        this._currentTaskDependencies = [];
        this._currentTask.dependency_relationships?.forEach((dependency) => {
            this._currentTaskDependencies.push(
                this._taskTreeItems?.find((item) => item.uuid === dependency.depends_on_task_uuid)
            );
        });

        this._currentTaskDependants = [];
        this._currentTask.dependent_relationships?.forEach((dependent) => {
            this._currentTaskDependants.push(this._taskTreeItems?.find((item) => item.uuid === dependent.task_uuid));
        });

        this._canEditStatus = this._canEditTask && TaskUtils.canChangeTaskStatus(this._currentTask);
        this._canEditDueDate = this._canEditTask;
        this._canEditStartDate = this._canEditTask;
        this._canEditEndDate = this._canEditTask;

        if (
            this._currentTask.task_type === ECpmCTaskTypeApiEnum.SMART &&
            this._currentTask.plugin_id &&
            this._currentTask.plugin_function_id
        ) {
            this._objectStoreEntriesTransientCache = new ObjectStoreEntriesTransientCache(
                this.globalsService.sessionService.getCustomer()!.uuid,
                {
                    organizationUUID: this.globalsService.sessionService.getOrganization()?.uuid,
                    projectUUID: this.globalsService.sessionService.getProject()?.uuid,
                    serviceRequestUUID: this.globalsService.sessionService.getServiceRequest()?.uuid
                }
            );
            await this._objectStoreEntriesTransientCache.init();

            const found = TreeUtils.findTreeNodeByKey(
                this._functionsTree,
                ActionJobsUtils.functionsTreeKey(this._currentTask.plugin_id, this._currentTask.plugin_function_id)
            );

            this._selectedPlugin = found?.parent?.data as CpmAEPluginSpecApiSchema;
            this._selectedPluginFunction = found?.data as CpmAEPluginFunctionApiSchema;

            this._inputsMappings =
                (this._currentTask!.inputs_mapping as
                    | Record<string, CpmCTaskInputOutputMappingApiSchema>
                    | undefined) ?? {};
            this._outputsMappings =
                (this._currentTask!.outputs_mapping as
                    | Record<string, CpmCTaskInputOutputMappingApiSchema>
                    | undefined) ?? {};
        } else {
            this._objectStoreEntriesTransientCache = undefined;
            this._selectedPlugin = undefined;
            this._selectedPluginFunction = undefined;
        }

        // Load options based on task type using the utility
        this._options = TaskOptionsUtils.getOptionsForTask(this._currentTask);

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Edit status
     */
    protected editStatus() {
        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: 'Task status',
                    schema: JsonSchemaUtils.autoEnumPropety(this._taskStatusHumanReadable),
                    data: StringUtils.toHumanFormatedStr(this._currentTask?.status.toString() ?? '')
                })
            )
            .onClose.subscribe(async (res: JsonSchemaFormModalResult) => {
                if (!res.validated) return;

                const newStatus = Object.values(ECpmCTaskStatusApiEnum).at(
                    this._taskStatusHumanReadable.indexOf(res.data as string)
                );

                await this.asyncCritical(async () => {
                    if (!this._currentTask) return;
                    const updatedTask = await firstValueFrom(
                        CpmCTaskApiService.updateTask(this._currentTask.uuid, {
                            status: newStatus
                        })
                    );

                    this._currentTask.status = updatedTask.status;
                    this._currentTask.attributes1 = updatedTask.attributes1;

                    const taskTreeItem = this._taskTreeItems?.find((item) => item.uuid === this._currentTask?.uuid);
                    if (taskTreeItem) {
                        taskTreeItem.status = updatedTask.status;
                        taskTreeItem.attributes1 = updatedTask.attributes1;
                    }
                });
            });
    }
    /**
     * Edit due date
     */
    protected editDueDate() {
        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: 'Task due date',
                    schema: JsonSchemaUtils.autoDatePropety(true),
                    data: this._currentTask?.due_date ? new Date(this._currentTask?.due_date) : null
                })
            )
            .onClose.subscribe(async (res: JsonSchemaFormModalResult) => {
                if (!res.validated) return;

                const newDueDate = res.data ? new Date(res.data as string) : null;
                await this.asyncCritical(async () => {
                    if (!this._currentTask) return;

                    const updatedTask = await firstValueFrom(
                        CpmCTaskApiService.updateTask(this._currentTask.uuid, {
                            due_date: DateUtils.toLocalISOString(newDueDate) ?? null
                        })
                    );
                    this._currentTask.due_date = updatedTask.due_date;
                    this.$refreshRequest.emit();
                });
            });
    }
    /**
     * Edit start date
     */
    protected editStartDate() {
        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: 'Task start date',
                    schema: JsonSchemaUtils.autoDatePropety(true),
                    data: this._currentTask?.start_date ? new Date(this._currentTask?.start_date) : null
                })
            )
            .onClose.subscribe(async (res: JsonSchemaFormModalResult) => {
                if (!res.validated) return;

                const newStartDate = res.data ? new Date(res.data as string) : null;
                await this.asyncCritical(async () => {
                    if (!this._currentTask) return;

                    const updatedTask = await firstValueFrom(
                        CpmCTaskApiService.updateTask(this._currentTask.uuid, {
                            start_date: DateUtils.toLocalISOString(newStartDate) ?? null
                        })
                    );
                    this._currentTask.start_date = updatedTask.start_date;
                    this.$refreshRequest.emit();
                });
            });
    }
    /**
     * Edit end date
     */
    protected editEndDate() {
        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: 'Task end date',
                    schema: JsonSchemaUtils.autoDatePropety(true),
                    data: this._currentTask?.end_date ? new Date(this._currentTask?.end_date) : null
                })
            )
            .onClose.subscribe(async (res: JsonSchemaFormModalResult) => {
                if (!res.validated) return;

                const newEndDate = res.data ? new Date(res.data as string) : null;
                await this.asyncCritical(async () => {
                    if (!this._currentTask) return;

                    const updatedTask = await firstValueFrom(
                        CpmCTaskApiService.updateTask(this._currentTask.uuid, {
                            end_date: DateUtils.toLocalISOString(newEndDate) ?? null
                        })
                    );
                    this._currentTask.end_date = updatedTask.end_date;
                    this.$refreshRequest.emit();
                });
            });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Ask confirmation before persisting input changes
     */
    protected async onInputsChanged(event: ActionJobsFunctionIoEditorChangeEvent) {
        if (!this._currentTask || !this._canEditTask) return;
        if (!this._inputsMappingValid || !this._outputsMappingValid) return;

        if (!this.shouldWarnResetOnChange) {
            await this.persistMappings();
            return;
        }

        if (event.confirmationHandled) {
            await this.persistMappings();
            return;
        }

        this.globalsService.dialogService
            .open(
                ConfirmationModalComponent,
                ConfirmationModalComponent.getDialogServiceParameters(this._taskInputChangeConfirmation)
            )
            .onClose.subscribe(async (confirmed: boolean) => {
                if (confirmed) {
                    await this.persistMappings();
                    return;
                }

                await this.refresh();
            });
    }

    /**
     * Persist mappings without extra confirmation
     */
    protected async onMappingsChanged() {
        const inputsMappingValid =
            this._inputsMappingValid ||
            (this._currentTask!.attributes1 & ECpmCTaskAttributes1ApiEnum.SMART_REQUIRES_MANUAL_INPUT) != 0;
        if (!inputsMappingValid || !this._outputsMappingValid) return;
        await this.persistMappings();
    }

    /**
     * Save current input/output mappings to the server
     */
    private async persistMappings(
        inputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = this._inputsMappings,
        outputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = this._outputsMappings
    ): Promise<boolean> {
        if (!this._currentTask) {
            return false;
        }

        this.showLoading = true;

        try {
            const toSubmit: CpmCTaskUpdateApiSchema = {
                inputs_mapping: inputsMappings,
                outputs_mapping: outputsMappings
            };

            const fromUpdate = await firstValueFrom(CpmCTaskApiService.updateTask(this._currentTask.uuid, toSubmit));

            await this.loadTask(this._currentTask.uuid, fromUpdate);
            return true;
        } catch (err) {
            console.log(err);
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: 'Failed to update task',
                detail: (err as {error?: {message?: string}})?.error?.message ?? 'Unknown'
            });
            return false;
        } finally {
            this.showLoading = false;
        }
    }

    /**
     * Persist input mappings from the editor
     */
    protected readonly persistInputMappingsFromEditor = async (
        nextMappings: Record<string, CpmCTaskInputOutputMappingApiSchema>,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _event: ActionJobsFunctionIoEditorChangeEvent
    ): Promise<boolean> => {
        if (!this._outputsMappingValid) {
            return false;
        }

        return await this.persistMappings(nextMappings, this._outputsMappings);
    };

    // ===============================================================
    // ===============================================================

    /**
     * When an option checkbox is changed
     */
    protected async onOptionChanged(option: TaskOptionDisplay) {
        if (!this._currentTask) return;

        this.showLoading = true;

        await this.asyncCritical(async () => {
            // Build update payload using the utility
            const toSubmit = TaskOptionsUtils.buildUpdatePayload(option.tag, option.checked);

            const updatedTask = await firstValueFrom(CpmCTaskApiService.updateTask(this._currentTask!.uuid, toSubmit));

            if (this._currentTask) {
                this._currentTask.attributes1 = updatedTask.attributes1;
            }
        });

        this.$refreshRequest.emit();
        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On task selected
     * @param task
     * @protected
     */
    protected selectTask(taskUUID: string) {
        this.$selectTaskRequest.emit(taskUUID);
    }

    /**
     * Get the live attributes1 value for the current task
     */
    protected get currentTaskLiveAttributes1(): ECpmCTaskAttributes1ApiEnum | undefined {
        if (!this._currentTask?.uuid) return undefined;

        return (
            this._taskTreeItems?.find((item) => item.uuid === this._currentTask?.uuid)?.attributes1 ??
            this._currentTask.attributes1
        );
    }

    /**
     * Check if the current task is marked as outdated in its attributes1 field
     */
    protected get currentTaskIsOutdated(): boolean {
        const attributes1 = this.currentTaskLiveAttributes1;
        return attributes1 !== undefined && (attributes1 & ECpmCTaskAttributes1ApiEnum.OUTDATED) !== 0;
    }

    /**
     * Check if the current task is marked as outdated and the last execution failed in its attributes1 field
     */
    protected get currentTaskIsOutdatedFailed(): boolean {
        const attributes1 = this.currentTaskLiveAttributes1;
        return (
            attributes1 !== undefined &&
            (attributes1 & ECpmCTaskAttributes1ApiEnum.OUTDATED) !== 0 &&
            (attributes1 & ECpmCTaskAttributes1ApiEnum.LAST_EXECUTION_FAILED) !== 0
        );
    }
}
